import { ReactNode } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { formatBRL } from "../../shared/ui/format";

// Formato de GET doctor/vacancies.
export type Duty = {
  id: number | string;
  date: string;
  value: number | null;
  hospital: string;
  company: string;
  startHour: string;
  endHour: string;
  externalSystemId?: number;
};

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
export const shortDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
// Dia da semana pela data ISO (UTC), sem deslocamento de fuso.
export const weekdayShort = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
};
export const isDutyAvailable = (duty: Duty) => duty.value != null && Number(duty.value) > 0;

export type DutyLineProps = { duty: Duty; action?: ReactNode; muted?: boolean; note?: ReactNode; mobile?: boolean; dateFirst?: boolean };

export default function DutyLine({ duty, action, muted = false, note, mobile = false, dateFirst = false }: DutyLineProps) {
  const ok = isDutyAvailable(duty);
  const day = String(duty.date).slice(0, 10);
  const value = (
    <Typography sx={{ fontSize: 15, fontWeight: 600, color: ok ? "primary.main" : "text.secondary", whiteSpace: "nowrap" }}>
      {ok ? `${formatBRL(duty.value)}*` : "Indisponível"}
    </Typography>
  );
  if (dateFirst) {
    // Passo 4 (mockup B): data e horário em destaque; unidade · empresa embaixo.
    return (
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, px: mobile ? 1 : 2, py: 1.5, opacity: muted ? 0.6 : 1 }}>
        {action && <Box sx={{ pt: 0.25 }}>{action}</Box>}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.4 }}>{`${weekdayShort(day)}, ${shortDate(day)} · ${duty.startHour}–${duty.endHour}`}</Typography>
            {value}
          </Box>
          <Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.4, overflowWrap: "anywhere" }}>{`${duty.hospital} · ${duty.company}`}</Typography>
          {note && <Typography sx={{ fontSize: 12.5, color: "#8a5200", mt: 0.25 }}>{note}</Typography>}
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, px: mobile ? 1 : 2, py: 1.5, opacity: muted ? 0.6 : 1 }}>
      {action && <Box sx={{ pt: 0.25 }}>{action}</Box>}
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexWrap: "wrap", columnGap: 2, rowGap: 0.5 }}>
        <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.4, overflowWrap: "anywhere" }}>{duty.hospital}</Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.4, overflowWrap: "anywhere" }}>{duty.company}</Typography>
        </Box>
        <Box
          sx={{
            flex: mobile ? "1 1 100%" : "0 0 auto",
            display: "flex",
            flexDirection: mobile ? "row" : "column",
            alignItems: mobile ? "center" : "flex-end",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: mobile ? 1 : 0.25,
          }}
        >
          <Typography sx={{ fontSize: 13, color: "text.secondary", display: "flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
            <CalendarTodayOutlinedIcon sx={{ fontSize: 14 }} aria-hidden />
            {`${shortDate(day)} · ${duty.startHour}–${duty.endHour}`}
          </Typography>
          {value}
        </Box>
        {note && <Typography sx={{ flex: "1 1 100%", fontSize: 12.5, color: "#8a5200" }}>{note}</Typography>}
      </Box>
    </Box>
  );
}
